import React, { useState, useEffect } from 'react';
import TipoClienteList from './TipoClienteList';
import TipoClienteModal from './TipoClienteModal';
import './TipoCliente.css';

// API base URL based on environment
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL || 'https://api.veridicus.com.br'
  : process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:5000';

// Helper to get auth header
const getAuthHeader = () => {
  return {
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  };
};

const TipoClientePage = () => {
  const [tiposCliente, setTiposCliente] = useState([]);
  const [filteredTipos, setFilteredTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTipoCliente, setSelectedTipoCliente] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('todos');

  const fetchTiposCliente = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(`${API_BASE_URL}/api/tipos-cliente/`, {
        headers: getAuthHeader()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const tiposArray = data?.data || data;
      const validTipos = Array.isArray(tiposArray) ? tiposArray : [];
      
      setTiposCliente(validTipos);
      setFilteredTipos(validTipos);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar tipos de cliente: ' + err.message);
      console.error('Erro ao buscar tipos de cliente:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTiposCliente();
  }, []);

  // Filter tipos based on active tab
  useEffect(() => {
    if (activeTab === 'todos') {
      setFilteredTipos(tiposCliente);
    } else if (activeTab === 'ativos') {
      setFilteredTipos(tiposCliente.filter(tipo => tipo.ativo === true));
    } else if (activeTab === 'inativos') {
      setFilteredTipos(tiposCliente.filter(tipo => tipo.ativo === false));
    }
  }, [activeTab, tiposCliente]);

  // Handle clicking on a tipo to view details
  const handleTipoSelect = (tipo) => {
    setSelectedTipoCliente(tipo);
    setShowForm(false);
    setIsModalOpen(true);
  };

  const handleAddNew = () => {
    setSelectedTipoCliente(null);
    setIsEditing(false);
    setShowForm(true);
    setIsModalOpen(true);
  };

  const handleEdit = (tipoCliente) => {
    setSelectedTipoCliente(tipoCliente);
    setIsEditing(true);
    setShowForm(true);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTipoCliente(null);
    setShowForm(false);
    setIsEditing(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este tipo de cliente?')) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/tipos-cliente/${id}`, {
          headers: getAuthHeader(),
          method: 'DELETE',
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }

        // Refresh the list
        fetchTiposCliente();
        
        // Close modal if the deleted item was selected
        if (selectedTipoCliente && selectedTipoCliente._id === id) {
          handleCloseModal();
        }
        
        // Show success message
        const result = await response.json();
        if (result.message) {
          alert(result.message);
        }
      } catch (err) {
        setError('Erro ao excluir tipo de cliente: ' + err.message);
        console.error('Erro ao excluir tipo de cliente:', err);
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tipos-cliente/${id}/toggle-status`, {
        headers: getAuthHeader(),
        method: 'PUT',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Refresh the list
      fetchTiposCliente();
      
      // Show success message
      if (result.message) {
        alert(result.message);
      }
    } catch (err) {
      setError('Erro ao alterar status: ' + err.message);
      console.error('Erro ao alterar status:', err);
    }
  };

  const handleFormSubmit = async (tipoClienteData) => {
    try {      
      const url = isEditing
        ? `${API_BASE_URL}/api/tipos-cliente/${selectedTipoCliente._id}`
        : `${API_BASE_URL}/api/tipos-cliente`;
      
      const method = isEditing ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: getAuthHeader(),
        body: JSON.stringify(tipoClienteData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Show success message
      if (result.message) {
        alert(result.message);
      }
      
      // Refresh the list
      fetchTiposCliente();
      
      // Close the modal
      handleCloseModal();
    } catch (err) {
      setError('Erro ao salvar tipo de cliente: ' + err.message);
      console.error('Erro ao salvar tipo de cliente:', err);
      alert(err.message);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleCancel = () => {
    setShowForm(false);
    // If we're adding a new tipo and cancel, close the modal
    if (!isEditing) {
      handleCloseModal();
    }
  };

  return (
    <div className="clientes-container">
      <div className="clientes-header">
        <h1>Tipos de Cliente</h1>
        <button className="clientes-btn clientes-btn-primary" onClick={handleAddNew}>
          Cadastrar Tipo de Cliente
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="clientes-card">
        <div className="tabs-header">
          <div 
            className={`tab-item ${activeTab === 'todos' ? 'active' : ''}`}
            onClick={() => handleTabChange('todos')}
          >
            Todos
          </div>
          <div 
            className={`tab-item ${activeTab === 'ativos' ? 'active' : ''}`}
            onClick={() => handleTabChange('ativos')}
          >
            Ativos
          </div>
          <div 
            className={`tab-item ${activeTab === 'inativos' ? 'active' : ''}`}
            onClick={() => handleTabChange('inativos')}
          >
            Inativos
          </div>
        </div>

        {loading ? (
          <div className="loading">Carregando tipos de cliente...</div>
        ) : (
          <TipoClienteList 
            tiposCliente={filteredTipos}
            onSelectTipo={handleTipoSelect}
            selectedId={selectedTipoCliente?._id}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleStatus={handleToggleStatus}
          />
        )}
      </div>

      <TipoClienteModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        tipoCliente={selectedTipoCliente}
        showForm={showForm}
        isEditing={isEditing}
        onEdit={() => handleEdit(selectedTipoCliente)}
        onDelete={() => {
          handleDelete(selectedTipoCliente._id);
        }}
        onSubmit={handleFormSubmit}
        onCancel={handleCancel}
      />
    </div>
  );
};

export default TipoClientePage;
